import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { fetchWithRetry, isOnline } from '../../remote-api';
import { fetchRemoteBranchStockMermaByProduct } from '../../infra/reportsRemoteStockMermaRepo';
import type { BranchStockMermaRow } from '../../domain/reportsTypes';
import type { DateRangePreset } from '../reportsScreen.constants';
import { getReportsDateRange } from '../reportsScreen.utils';

const MERMA_PAGE_SIZE = 25;

type Params = {
  currentBranchId: string | null | undefined;
  isOperacionSection: boolean;
  isFocused: boolean;
  reportsBootstrapReady: boolean;
  canInventory: boolean;
  dateRangePreset: DateRangePreset;
  customRangeFrom: Date;
  customRangeTo: Date;
  refreshKey: number;
};

export function useReportsStockMerma({
  currentBranchId,
  isOperacionSection,
  isFocused,
  reportsBootstrapReady,
  canInventory,
  dateRangePreset,
  customRangeFrom,
  customRangeTo,
  refreshKey,
}: Params) {
  const [rows, setRows] = useState<BranchStockMermaRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [sumShrinkage, setSumShrinkage] = useState(0);
  const [sumPurchased, setSumPurchased] = useState(0);
  const [sumSold, setSumSold] = useState(0);
  const [sumBreakage, setSumBreakage] = useState(0);
  const [sumExpiry, setSumExpiry] = useState(0);
  const [sumExpiryCost, setSumExpiryCost] = useState(0);
  const [sumShrinkageCost, setSumShrinkageCost] = useState(0);
  const [source, setSource] = useState<'server' | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  /** null = todas; 'kg' = solo kg/g (útil verdulería/carnicería sin hardcode de rubro). */
  const [uomFilter, setUomFilter] = useState<'all' | 'kg'>('all');

  const goToNextPage = useCallback(() => {
    setPage((p) => p + 1);
  }, []);

  const goToPrevPage = useCallback(() => {
    setPage((p) => Math.max(0, p - 1));
  }, []);

  useLayoutEffect(() => {
    setPage(0);
  }, [currentBranchId, dateRangePreset, customRangeFrom, customRangeTo, uomFilter]);

  useEffect(() => {
    if (
      !currentBranchId ||
      !isOperacionSection ||
      !isFocused ||
      !reportsBootstrapReady ||
      !canInventory
    ) {
      setRows([]);
      setTotalCount(0);
      setSumShrinkage(0);
      setSumPurchased(0);
      setSumSold(0);
      setSumBreakage(0);
      setSumExpiry(0);
      setSumExpiryCost(0);
      setSumShrinkageCost(0);
      setSource(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    void (async () => {
      const online = await isOnline();
      if (!online) {
        if (!cancelled) {
          setRows([]);
          setTotalCount(0);
          setSource(null);
          setLoading(false);
        }
        return;
      }

      const range = getReportsDateRange(
        new Date(),
        dateRangePreset,
        dateRangePreset === 'custom' ? { from: customRangeFrom, to: customRangeTo } : null,
      );

      try {
        const data = await fetchWithRetry(() =>
          fetchRemoteBranchStockMermaByProduct({
            branchId: currentBranchId,
            fromIso: range.fromIso,
            toIso: range.toIso,
            limit: MERMA_PAGE_SIZE,
            offset: page * MERMA_PAGE_SIZE,
            uomFilter: uomFilter === 'kg' ? 'kg' : null,
          }),
        );
        if (cancelled) return;
        if (!data) {
          setRows([]);
          setTotalCount(0);
          setSource(null);
          return;
        }
        setRows(data.items);
        setTotalCount(data.totalCount);
        setSumPurchased(data.sumPurchased);
        setSumSold(data.sumSold);
        setSumShrinkage(data.sumShrinkage);
        setSumBreakage(data.sumBreakage);
        setSumExpiry(data.sumExpiry);
        setSumExpiryCost(data.sumExpiryCost);
        setSumShrinkageCost(data.sumShrinkageCost);
        setSource('server');
      } catch (error) {
        console.warn('[reports] stock merma', error);
        if (!cancelled) {
          setRows([]);
          setTotalCount(0);
          setSource(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    currentBranchId,
    isOperacionSection,
    isFocused,
    reportsBootstrapReady,
    canInventory,
    dateRangePreset,
    customRangeFrom,
    customRangeTo,
    page,
    uomFilter,
    refreshKey,
  ]);

  return {
    stockMermaRows: rows,
    stockMermaTotalCount: totalCount,
    stockMermaSumPurchased: sumPurchased,
    stockMermaSumSold: sumSold,
    stockMermaSumShrinkage: sumShrinkage,
    stockMermaSumBreakage: sumBreakage,
    stockMermaSumExpiry: sumExpiry,
    stockMermaSumExpiryCost: sumExpiryCost,
    stockMermaSumShrinkageCost: sumShrinkageCost,
    stockMermaSource: source,
    stockMermaLoading: loading,
    stockMermaPage: page,
    stockMermaPageSize: MERMA_PAGE_SIZE,
    stockMermaUomFilter: uomFilter,
    setStockMermaUomFilter: setUomFilter,
    goToNextStockMermaPage: goToNextPage,
    goToPrevStockMermaPage: goToPrevPage,
    setStockMermaPage: setPage,
  };
}
